import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStoredUTM, utmToParams } from '../../../hooks/useUTM';

const FORM_URL = 'https://readdy.ai/api/form/d7986vj86jhav3jpfh10';

type FormStatus = 'idle' | 'sending' | 'success' | 'error';

const packageOptions = ['Štart (590 €)', 'Biznis (990 €)', 'Pro (od 1 890 €)', 'Neviem, poraďte mi'];

export default function TWContactForm() {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    package: '',
    message: '',
    gdpr: false,
  });
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      if (name === 'message') setCharCount(value.length);
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.gdpr || charCount > 500) return;
    setStatus('sending');
    try {
      const utm = getStoredUTM();
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        package: form.package,
        message: form.message,
        source: 'Tvorba webov - konverzná stránka',
        ...utmToParams(utm),
      });
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        navigate('/dakujeme?service=Tvorba+webov');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="kontaktny-formular" className="py-24 bg-white relative overflow-hidden">
      <div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.06) 0%, transparent 60%)' }}
      />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">

          {/* Left */}
          <div>
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-mail-send-line text-orange-500 text-sm" />
              <span className="text-orange-600 text-sm font-semibold">Napíšte nám</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-zinc-900 mb-4 leading-tight">
              Pripravení mať web,{' '}
              <span className="text-orange-500">ktorý predáva?</span>
            </h2>
            <p className="text-zinc-500 text-base leading-relaxed mb-10">
              Napíšte nám a spoločne zistíme, čo váš web potrebuje na to, aby pracoval naplno.
              Konzultácia je zadarmo a nezáväzná.
            </p>

            <div className="space-y-5">
              {[
                { icon: 'ri-time-line', title: 'Odpovieme do 2 hodín', text: 'Každú správu berie vážne reálny človek, nie automat.' },
                { icon: 'ri-lock-line', title: 'Bez záväzkov', text: 'Konzultácia vás k ničomu nezaväzuje. Len si porozprávame.' },
                { icon: 'ri-shield-check-line', title: 'Záruka spokojnosti', text: 'Nespustíme web, s ktorým nie ste spokojní. Jednoducho.' },
                { icon: 'ri-eye-off-line', title: 'Vaše údaje sú v bezpečí', text: 'Nikdy nebudeme zdieľať vaše kontaktné informácie.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 border border-orange-100">
                    <i className={`${item.icon} text-orange-500 text-base`} />
                  </div>
                  <div>
                    <p className="text-zinc-900 font-bold text-sm">{item.title}</p>
                    <p className="text-zinc-400 text-xs mt-0.5">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - form */}
          <div>
            <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-7">
              {status === 'success' ? (
                <div className="flex flex-col items-center justify-center text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-emerald-50 border border-emerald-200 rounded-full mb-5">
                    <i className="ri-check-line text-emerald-500 text-3xl" />
                  </div>
                  <h3 className="text-zinc-900 text-2xl font-black mb-2">Odoslané!</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed max-w-xs">
                    Ďakujeme za záujem. Ozveme sa vám do 2 hodín.
                  </p>
                </div>
              ) : (
                <form
                  ref={formRef}
                  data-readdy-form
                  id="tw-contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Meno a priezvisko *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Ján Novák"
                        className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Telefón</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+421 9xx xxx xxx"
                        className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="jan@firma.sk"
                      className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200"
                    />
                  </div>

                  <div>
                    <label className="text-zinc-600 text-xs font-semibold mb-1.5 block">Ktorý balík vás zaujíma?</label>
                    <select
                      name="package"
                      value={form.package}
                      onChange={handleChange}
                      className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-2.5 text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200 cursor-pointer"
                    >
                      <option value="">Vyberte balík...</option>
                      {packageOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-zinc-600 text-xs font-semibold">Opíšte váš projekt *</label>
                      <span className={`text-xs ${charCount > 450 ? 'text-red-400' : 'text-zinc-400'}`}>
                        {charCount}/500
                      </span>
                    </div>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={4}
                      placeholder="Čo robí vaša firma, čo chcete dosiahnuť webom a kde sa teraz nachádzate..."
                      className="w-full bg-white border border-zinc-200 rounded-xl px-4 py-3 text-sm text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-orange-400/30 focus:border-orange-400 transition-all duration-200 resize-none"
                    />
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative mt-0.5 flex-shrink-0">
                      <input
                        type="checkbox"
                        name="gdpr"
                        checked={form.gdpr}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all duration-200 ${form.gdpr ? 'bg-orange-500 border-orange-500' : 'bg-white border-zinc-300 group-hover:border-zinc-400'}`}>
                        {form.gdpr && <i className="ri-check-line text-white text-xs" />}
                      </div>
                    </div>
                    <p className="text-zinc-400 text-xs leading-relaxed">
                      Súhlasím so spracovaním osobných údajov podľa{' '}
                      <a href="/ochrana-sukromia" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">
                        zásad ochrany súkromia
                      </a>
                      .
                    </p>
                  </label>

                  {status === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-600 text-xs px-4 py-3 rounded-xl">
                      Odosielanie zlyhalo. Skúste to znova alebo nás kontaktujte priamo.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending' || !form.gdpr}
                    className="w-full bg-orange-500 hover:bg-orange-400 disabled:bg-zinc-200 disabled:text-zinc-400 text-white font-black py-4 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-sm"
                  >
                    {status === 'sending' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Odosielam...
                      </>
                    ) : (
                      <>
                        Odoslať dopyt — konzultácia zadarmo
                        <i className="ri-send-plane-line" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-zinc-400 text-xs">
                    Odpovieme do 2 hodín. Bez záväzkov.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
